import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import DefaultLayout from "../../containers/layouts";
import { Rows, VehicleTest } from "../../components/tables/DataTable";
import DataTable from "../../components/tables/DataTable";
import { vehicleTestsColumns } from "../../components/utils/tableUtils";
import { GridFilterModel } from "@mui/x-data-grid/models/gridFilterModel";
import { GridFilterItem } from "@mui/x-data-grid/models/gridFilterItem";
import { getCheckedRowsFiles, getTesterFiles } from "../../services/http";
import { Box, Button, Chip, Divider } from "@mui/material";
import { useTestParams } from "./useTestParams";
import { GridPaginationModel } from "@mui/x-data-grid/models/gridPaginationProps";
import * as XLSX from "xlsx";
import { Dialog, DialogTitle, DialogActions } from "@mui/material";
import { GridCallbackDetails, GridRowId } from "@mui/x-data-grid";
import {truncateQueryParams} from "../../services/auth";

const Tests = () => {
  const { kfn, fn } = useTestParams();
  const kfnt = truncateQueryParams(kfn);
  const fnt = truncateQueryParams(fn);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [filter, setFilter] = useState<GridFilterItem[]>([]);
  const [rows, setRows] = useState<Rows>({ list: [], count: 0 });
  const [queryParams, setQueryParams] = useState(
    [
      {
        name: "kfn",
        value: kfnt,
      },
      {
        name: "fn",
        value: fnt,
      },
    ].filter((item) => item.value.length > 0)
  );

  const [checkedRowIds, setCheckedRowIds] = useState<string[]>([]);

  const handleSelectionModelChange = (
    selection: GridRowId[],
    details: GridCallbackDetails<any>
  ) => {
    const selectedIds = selection.map((id) => id.toString());
    setCheckedRowIds(selectedIds);
  };

  const [showNoFilterDialog, setShowNoFilterDialog] = useState(false);
  const fetchData = async (page = 0, size = 200) => {
    const data = await getTesterFiles(page, size, filter, queryParams, { kfn: kfnt, fn: fnt });
    setRows(data);
  };

  const fetchAllFilteredData = async () => {
    return await getTesterFiles(0, 1000000, filter, queryParams, { kfn: kfnt, fn: fnt });
  };

  const onFilterModelChange = (newFilter: GridFilterModel) => {
    const newItems = [...filter];
    let hasChange = false;
    newFilter.items.forEach((newItem) => {
      const index = newItems.findIndex((item) => item.field === newItem.field);
      if (index > -1) {
        if (newItems[index].value !== newItem.value) {
          hasChange = true;
        }
        newItems[index] = newItem;
      } else {
        newItems.push(newItem);
        hasChange = true;
      }
    });
    if (hasChange) {
      setFilter(newItems);
    }
  };

  const onPaginationModelChange = (pagination: GridPaginationModel) => {
    fetchData(pagination.page, pagination.pageSize * 10);
  };

  const translateField = (field: string) => {
    return vehicleTestsColumns.find((item) => item.field === field)?.headerName;
  };

  useEffect(() => {
    fetchData();
  }, [filter, queryParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const chips = filter.filter((item) => item.value);

  const HEADERS = [
    "Diag-Adresse (Master/SUB)",
    "Konzernfahrzeugnummer",
    "Fahrgestellnummer",
    "VW/Audi-Teilenummer",
    "Hardwareteilenummer",
    "HW-Version",
    "Bootloader",
    "SW-Version",
    "Schlüsseltyp",
    "Flashbar mit Tester",
    "Zeitstempel",
  ];

  const handleExport = async () => {
    if (!hasFilters()) {
      setShowNoFilterDialog(true);
      return;
    }

    let rowsToExport = [];

    if (hasCheckedRows()) {
      rowsToExport = await fetchCheckedRowsData();
    } else {
      rowsToExport = await fetchAllFilteredDataRows();
    }

    exportToExcel(HEADERS, rowsToExport);
  };

  const hasFilters = (): boolean => {
    return kfnt.length > 0 || fnt.length > 0 || filter.length > 0;
  };

  const hasCheckedRows = (): boolean => {
    return checkedRowIds.length > 0;
  };

  const fetchCheckedRowsData = async (): Promise<any[]> => {
    const checkedRowsData = await getCheckedRowsFiles(checkedRowIds);
    return formatRows(checkedRowsData.list);
  };

  const fetchAllFilteredDataRows = async (): Promise<any[]> => {
    const allFilteredData = await fetchAllFilteredData();
    return formatRows(allFilteredData.list);
  };

  const formatRows = (data: VehicleTest[]): any[] => {
    return data.map((row: VehicleTest) => ({
      "Diag-Adresse (Master/SUB)": row.testAddress,
      Konzernfahrzeugnummer: row.kfn,
      Fahrgestellnummer: row.fn,
      "VW/Audi-Teilenummer": row.audiPartNumber,
      Hardwareteilenummer: row.hardwarePartNumber,
      "HW-Version": row.hwVersion,
      Bootloader: row.bootloader,
      "SW-Version": row.swVersion,
      Schlüsseltyp: row.keyType,
      "Flashbar mit Tester": row.flashTester,
      Zeitstempel: new Date(row.timestamp).toLocaleString(),
    }));
  };

  const exportToExcel = (headers: string[], rows: any[]) => {
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    XLSX.utils.sheet_add_json(ws, rows, {
      origin: -1,
      skipHeader: true,
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, "data_export.xlsx");
  };

  const handleCloseDialog = () => {
    setShowNoFilterDialog(false);
  };

  return (
    <DefaultLayout>
      <Box display="flex" justifyContent="flex-end" p={2}>
        <Button variant="contained" color="primary" onClick={handleExport}>
          EXPORT Data
        </Button>

        <Dialog open={showNoFilterDialog} onClose={handleCloseDialog}>
          <DialogTitle>
            {
              "Bitte wählen Sie mindestens einen Filter aus, bevor Sie exportieren."
            }
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Divider />
      <Chips>
        {queryParams.length > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              setQueryParams([]);
            }}
          >
            Externe Filter zurücksetzen
          </Button>
        )}
        {queryParams.map((item) => (
          <Chip
            key={item.name}
            label={
              <Params>
                {translateField(item.name)}
                {` enthält eines davon `}
                {item.value.toString()}
              </Params>
            }
            onDelete={() => {
              setQueryParams(
                queryParams.filter((param) => param.name !== item.name)
              );
            }}
          />
        ))}
      </Chips>
      <Chips>
        {chips.length === 0 &&
          queryParams.length === 0 &&
          "Die Liste der Filter ist leer."}
        {chips.length > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              setFilter([]);
              setFilterModel({ items: [] });
            }}
          >
            Ausgewählte Filter zurücksetzen
          </Button>
        )}
        {chips.map((item) => (
          <Chip
            key={item.field}
            label={`${translateField(item.field)} enthält ${item.value}`}
            onDelete={() => {
              setFilter(filter.filter((grid) => grid !== item));
              setFilterModel((model) => ({
                ...model,
                items: model.items.filter(
                  (modelItem) => modelItem.field !== item.field
                ),
              }));
            }}
          />
        ))}
      </Chips>
      <Table
        rows={rows}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        columns={vehicleTestsColumns}
        onFilterModelChange={onFilterModelChange}
        onPaginationModelChange={onPaginationModelChange}
        onRowSelectionModelChange={handleSelectionModelChange}
        checkboxSelection
      />
    </DefaultLayout>
  );
};

export default Tests;

const Table = styled(DataTable)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  height: calc(100% - 165px);
  overflow: auto;
`;

const Chips = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 16px;
    padding: 0 16px;
    margin-top: 20px;
`;

const Params = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  max-width: 30vw;
  overflow: auto;
    
    :hover {
        height: 50px;
    }
`;
